import React, { useMemo } from 'react';
import Avatar from '@atlaskit/avatar';
import { HeadingItem } from '@atlaskit/menu';
import { PROJECTS_ITEM_TYPE } from '@atlassian/jira-favourite-change-provider/src/model/constants.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { getLinkFromProject } from '@atlassian/jira-navigation-apps-common/src/utils/index.tsx';
import { testIdConcat } from '@atlassian/jira-navigation-apps-common/src/utils/test-id.tsx';
import { DEFAULT_LOAD_COUNT_FAV } from '../../../../common/constants.tsx';
import { FavoriteButton } from '../../../../common/ui/menu/favourite-button/main.tsx';
import { MenuItem } from '../../../../common/ui/menu/item/index.tsx';
import { MenuList, MenuListItem } from '../../../../common/ui/menu/list/index.tsx';
import { MenuItemViewAll } from '../../../../common/ui/menu/view-all/main.tsx';
import { useProjectNavItems as useItems } from '../../../../controllers/items/index.tsx';
import messages from './messages.tsx';
import type { FavoriteContentProps } from './types.tsx';

export const FavoriteContent = ({ testIdPrefix }: FavoriteContentProps) => {
	const { formatMessage } = useIntl();
	const { favoriteItems, changeFavorite: onChangeFavorite, changedItems } = useItems();

	const itemsToShow = useMemo(
		() => favoriteItems.slice(0, DEFAULT_LOAD_COUNT_FAV),
		[favoriteItems],
	);

	const testId = testIdConcat(testIdPrefix, 'items-starred');

	const projectsUrl = '/jira/projects?page=1&sortKey=favourite&sortOrder=DESC';

	return favoriteItems.length > 0 ? (
		<span data-testid={testId}>
			<HeadingItem testId={testIdConcat(testId, 'heading')}>
				{formatMessage(messages.starred)}
			</HeadingItem>
			<MenuList>
				{itemsToShow.map((item, idx) => {
					const { url, id, favourite: isFavourite, metadata, title } = item;
					const contextItem = changedItems[item.id.toString()];
					const isPending =
						contextItem && contextItem.pending === true ? contextItem.pending : false;

					const { params, query, route: to } = getLinkFromProject(item);

					return (
						<MenuListItem key={id} ariaLabel={formatMessage(messages.starred)}>
							<MenuItem
								analytics={{
									actionSubjectId: 'projectsNavigationMenuItem',
									menuItemId: id,
									menuItemType: 'starred',
									position: idx,
									starred: Boolean(isFavourite),
								}}
								description={metadata}
								href={url}
								iconBefore={
									<Avatar
										appearance="square"
										size="small"
										borderColor="transparent"
										src={item.avatarUrl}
									/>
								}
								iconAfter={
									<FavoriteButton
										id={String(id)}
										isFavorite={Boolean(isFavourite)}
										favoriteItemName={title}
										isPending={isPending}
										menuItemType="starred"
										onChangeFavorite={onChangeFavorite}
										type={PROJECTS_ITEM_TYPE}
									/>
								}
								params={params}
								query={query}
								testId={testIdConcat(testId, `item-${idx}`)}
								to={to}
								aria-label={`${title} ${metadata || ''}`}
							>
								{title}
							</MenuItem>
						</MenuListItem>
					);
				})}
			</MenuList>
			{favoriteItems.length >= DEFAULT_LOAD_COUNT_FAV && (
				<MenuItemViewAll
					analytics={{
						actionSubjectId: 'projectsNavigationMenuItem',
						menuItemId: 'viewAll',
						// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
						position: DEFAULT_LOAD_COUNT_FAV as number,
					}}
					href={projectsUrl}
					testId={testIdConcat(testId, 'item-view-all')}
					to={projectsUrl}
				/>
			)}
		</span>
	) : null;
};
